import Switch from "@mui/material/Switch";
import { observer } from "mobx-react-lite";
import { useChangeLeadStatusStore, useSetReminderStore } from "../..";
import useRowClassName from "../../hooks/useRowClassName";
import IEntityFieldsProps from "../../interfaces/IEntityFieldsProps";
import { ILeadEditState, LeadOwnershipType, LeadStatus } from "../../interfaces/ILead";
import { leadOwnershipTypeToString, leadStatusToString } from "../../utils/enumUtils";
import commonStyles from "../Common.module.css";
import editStyles from "../Edit.module.css";
import DateInput from "../inputs/DateInput";
import NumberInput from "../inputs/NumberInput";
import PhoneNumberInput from "../inputs/PhoneNumberInput";
import PipedriveCompanyInput from "../inputs/PipedriveCompanyInput";
import PipedriveLabelInput from "../inputs/PipedriveLabelInput";
import SelectInput from "../inputs/SelectInput";
import StringInput from "../inputs/StringInput";
import styles from "./LeadFields.module.css";

function LeadFields({ formik, onChangeStatus }: ILeadFieldsProps) {
    const setReminderStore = useSetReminderStore();
    const changeLeadStatusStore = useChangeLeadStatusStore();
    const rowClassName = useRowClassName();

    const onStatusChange = (status: string | null) => {
        changeLeadStatusStore.open(status as LeadStatus, onChangeStatus);
        return { status: formik.values.status };
    };

    return (
        <div className={editStyles.container}>
            <div className={editStyles.column}>
                <div className={commonStyles.header}>Lead</div>
                <div className={rowClassName}>
                    <StringInput formik={formik} label="ADDRESS" name="address" />
                </div>
                <div className={rowClassName}>
                    <SelectInput formik={formik} label="STATUS" name="status" valueAsCorrectType={value => value as LeadStatus} onChange={onStatusChange}>
                        {Object.values(LeadStatus).map(status => (
                            <option key={status} value={status}>{leadStatusToString(status)}</option>
                        ))}
                    </SelectInput>
                    <PipedriveLabelInput formik={formik} label="LABEL" name="labelId" />
                </div>
                <div className={rowClassName}>
                    <StringInput formik={formik} label="ZONING" name="zoning" />
                </div>
                <div className={rowClassName}>
                    <NumberInput formik={formik} label="LAND SIZE (ACRES)" name="landSize" />
                    <NumberInput formik={formik} label="USABLE LAND SIZE (ACRES)" name="usableArea" />
                    <NumberInput formik={formik} label="BUILDING SIZE (SQ-FT)" name="buildingSize" />
                </div>
                <div className={rowClassName}>
                    <NumberInput formik={formik} label="LOT COVERAGE (%)" name="lotCoverage" />
                    <NumberInput formik={formik} label="ASSESSMENT ($)" name="totalAssessment" />
                </div>
                <div className={rowClassName}>
                    <StringInput formik={formik} label="OWNER NAME" name="ownerName" />
                </div>
                <div className={rowClassName}>
                    <SelectInput formik={formik} label="OWNERSHIP TYPE" name="ownershipType" valueAsCorrectType={value => value as LeadOwnershipType}>
                        <option value="">----</option>
                        {Object.values(LeadOwnershipType).map(ownershipType => (
                            <option key={ownershipType} value={ownershipType}>{leadOwnershipTypeToString(ownershipType)}</option>
                        ))}
                    </SelectInput>
                </div>
                <div className={rowClassName}>
                    <StringInput formik={formik} label="OWNER ADDRESS" name="ownerAddress" />
                </div>
                <div className={rowClassName}>
                    <DateInput formik={formik} label="LAST SOLD DATE" name="saleDate" />
                    <NumberInput formik={formik} label="LAST SOLD PRICE ($)" name="salePrice" />
                </div>
            </div>
            <div className={editStyles.column}>
                <div className={commonStyles.header}>Contacts</div>
                <div className={rowClassName}>
                    <PipedriveCompanyInput formik={formik} label="COMPANY" name="pipedriveCompanyName" />
                </div>
                <div className={rowClassName}>
                    <StringInput formik={formik} label="CONTACT NAME" name="contactName" />
                    <StringInput formik={formik} label="EMAIL" name="email" />
                </div>
                <div className={rowClassName}>
                    <PhoneNumberInput formik={formik} label="PHONE #" name="phoneNumber" />
                </div>
            </div>
            <div className={editStyles.column}>
                <div className={commonStyles.header}>Reminder</div>
                <div className={rowClassName}>
                    <label className={styles.switchRow}>
                        <span className={styles.switchLabel}>REMINDER</span>
                        <Switch
                            checked={formik.values.isSetReminder}
                            onChange={e => {
                                if (e.target.checked) {
                                    setReminderStore.open(date => {
                                        formik.setFieldValue("isSetReminder", true);
                                        formik.setFieldValue("activityDueDate", date);
                                    });
                                } else {
                                    formik.setFieldValue("isSetReminder", false);
                                }
                            }}
                        />
                    </label>
                </div>
                {formik.values.isSetReminder && (
                    <>
                        <div className={rowClassName}>
                            <DateInput formik={formik} label="DUE DATE" name="activityDueDate" />
                        </div>
                        <div className={rowClassName}>
                            <StringInput formik={formik} label="ACTIVITY" name="activitySubject" />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default observer(LeadFields);

interface ILeadFieldsProps extends IEntityFieldsProps<ILeadEditState> {
    onChangeStatus: (status: LeadStatus, note: string) => Promise<void>;
}
